import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Calendar, Users, Heart, XCircle, Sparkles, ArrowRight, Lightbulb } from "lucide-react";

const GoogleWaveHistory = () => {
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Google Wave History: The Ahead-of-Its-Time Collaboration Platform That Failed",
    "description": "The complete history of Google Wave, from its ambitious 2009 launch to its shutdown in April 2012, and how its innovations live on in modern collaboration tools.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/google-wave/history/"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "Google Wave",
      "applicationCategory": "CollaborationApplication",
      "operatingSystem": "Web Browser"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Google Wave History: The Ahead-of-Its-Time Collaboration Platform That Failed | CSW</title>
        <meta name="description" content="The complete history of Google Wave, from its ambitious 2009 launch to its shutdown in April 2012, and how its innovations live on in modern collaboration tools." />
        <meta name="keywords" content="Google Wave history, Google Wave shutdown, Lars Rasmussen, Jens Rasmussen, real-time collaboration, Google I/O 2009" />
        <link rel="canonical" href="https://chillstudy.work/google-wave/history/" />
        <meta property="og:title" content="Google Wave History: The Ahead-of-Its-Time Collaboration Platform That Failed" />
        <meta property="og:description" content="The complete history of Google Wave, from its 2009 launch to its shutdown in 2012." />
        <meta property="og:url" content="https://chillstudy.work/google-wave/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-muted-foreground">
              <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/google-wave/" className="hover:text-primary transition-colors">Google Wave Alternative</Link></li>
              <li>/</li>
              <li className="text-foreground">History</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Google Wave: The Ahead-of-Its-Time Collaboration Platform That Failed
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              In 2009 <a href="https://en.wikipedia.org/wiki/Google_Wave" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google</a> unveiled 
              Google Wave, a radical experiment that promised to reinvent communication. Wave combined email, instant messaging and collaborative 
              document editing into a single, real-time platform.
            </p>
          </header>

          {/* Timeline Section */}
          <section className="mb-16">
            <div className="space-y-8">
              {/* Ambitious Vision */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold">Ambitious Vision and Features (2009)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Wave's creators, <a href="https://en.wikipedia.org/wiki/Lars_Rasmussen_(software_developer)" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Lars</a> and{" "}
                  <a href="https://en.wikipedia.org/wiki/Jens_Rasmussen_(software_developer)" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Jens Rasmussen</a>—brothers 
                  who had previously built <strong className="text-foreground">Google Maps</strong>—imagined a tool that would replace fragmented communications with a unified, collaborative "wave."
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  A wave was both a document and a conversation. Multiple people could type simultaneously, edit each other's messages and 
                  drag-and-drop files or gadgets (like polls or maps) into the thread. Because all edits were recorded, users could 
                  <strong className="text-foreground"> replay a wave from the beginning</strong> to follow the evolution of a discussion.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Wave also provided APIs and an open protocol, inviting developers to build extensions and integrate waves into other applications.
                </p>
              </article>

              {/* Initial Hype */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Calendar className="w-6 h-6 text-accent" />
                  </div>
                  <h2 className="text-2xl font-bold">Initial Hype and Confusion (2009-2010)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Google introduced Wave at its <a href="https://io.google/2009/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">I/O developer conference</a> in 
                  May 2009, showcasing collaborative planning, real-time translation and rich media insertion. Invitations to the limited 
                  preview were coveted.
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Early adopters marvelled at the technology but soon discovered that Wave lacked a clear purpose. Was it email, a forum, 
                  a document editor or all of the above?
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  A <a href="https://www.failory.com/google/wave" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Failory analysis</a> later 
                  noted that Wave was a unified workspace ahead of its time, integrating email, chat and document collaboration, but that 
                  users were confused by its complexity and the lack of focused use cases. Without a simple onboarding path, many people 
                  tried Wave once and never returned.
                </p>
              </article>

              {/* Why People Were Excited */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-500/10 rounded-lg">
                    <Heart className="w-6 h-6 text-pink-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Revolutionary Features</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Real-Time Collaboration</h3>
                    <p>Watch others type character by character. Multiple people could edit the same content simultaneously.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Playback Feature</h3>
                    <p>Replay the entire discussion from the beginning to see how it evolved—a feature that was revolutionary for its time.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Rich Media Embedding</h3>
                    <p>Embed photos, maps, polls and other interactive gadgets directly into conversations.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Open Protocol</h3>
                    <p>Wave offered APIs and an open federation protocol, allowing developers to extend its functionality.</p>
                  </div>
                </div>
              </article>

              {/* The Shutdown */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-destructive/10 rounded-lg">
                    <XCircle className="w-6 h-6 text-destructive" />
                  </div>
                  <h2 className="text-2xl font-bold">Decline and Shutdown (2010-2012)</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  By mid-2010, Google acknowledged that Wave had not caught on. On <strong className="text-foreground">August 4, 2010</strong>, 
                  Google announced it would cease active development because Wave had "not seen the user adoption we would have liked".
                </p>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The service lingered in a read-only state until <strong className="text-foreground">January 31, 2012</strong>, when waves 
                  became uneditable and only viewable. Finally, on <strong className="text-foreground">April 30, 2012</strong>, Google shut 
                  down the servers entirely, and users could no longer access their waves.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Google released parts of the code as "Wave in a Box," and the{" "}
                  <a href="https://incubator.apache.org/projects/wave.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Apache Software Foundation</a> briefly 
                  maintained a successor called Apache Wave, but development fizzled out.
                </p>
              </article>

              {/* Why Wave Failed */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Users className="w-6 h-6 text-orange-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Why Wave Failed</h2>
                </div>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Undefined Use Case</h3>
                    <p>Wave tried to replace email, instant messaging and document editing simultaneously. Users didn't know when to use it.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Overwhelming Interface</h3>
                    <p>The live-editing interface was technologically impressive but could be distracting. Seeing every keystroke made conversations messy.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Invitation Rollout</h3>
                    <p>By limiting invites, Google created hype but prevented network effects. Many users had access but none of their colleagues did.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Competing Priorities</h3>
                    <p>Google may have been reluctant to cannibalize Gmail and other products. When Wave didn't take off quickly, resources were redirected.</p>
                  </div>
                </div>
              </article>

              {/* Legacy */}
              <article className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Lightbulb className="w-6 h-6 text-blue-500" />
                  </div>
                  <h2 className="text-2xl font-bold">Legacy and Impact</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Despite its failure, Google Wave introduced concepts that have become standard in collaborative tools:
                </p>
                <div className="space-y-4 text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Real-Time Editing</h3>
                    <p>Wave's operational transform algorithm paved the way for simultaneous multi-user editing in{" "}
                      <a href="https://docs.google.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Docs</a> and other modern editors.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Threaded Conversations</h3>
                    <p>Many chat and document tools now allow threaded discussions and inline comments, echoing Wave's hybrid of communication and content.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Collaboration Platforms</h3>
                    <p><a href="https://slack.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Slack</a>,{" "}
                      <a href="https://www.microsoft.com/en-us/microsoft-teams/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Teams</a> and{" "}
                      <a href="https://www.notion.so/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Notion</a> integrate 
                      chat, documents and apps—ideas that Wave explored, albeit in a more focused, user-friendly way.</p>
                  </div>
                </div>
              </article>
            </div>
          </section>

          {/* Sources */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Sources</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://support.google.com/answer/1083134" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google's support page</a> "Status of Google Wave," 
                  stating that active development was discontinued in August 2010, the service became read-only on January 31, 2012, and all servers were shut down on April 30, 2012.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://www.failory.com/google/wave" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Failory's retrospective</a> "What Was Google Wave and Why Was It Discontinued?" 
                  describing Wave as a unified workspace integrating email, chat and document collaboration, and explaining that hype gave way to confusion and low user retention.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRight className="w-4 h-4 mt-1 text-primary flex-shrink-0" />
                <span>
                  <a href="https://en.wikipedia.org/wiki/Google_Wave" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Wikipedia article</a> on 
                  Google Wave, providing comprehensive coverage of its development, features, and eventual shutdown.
                </span>
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-2xl font-bold mb-4">Looking for a Google Wave Alternative?</h2>
            <p className="text-muted-foreground mb-6">
              Wave's vision of real-time collaboration lives on. CSW offers collaborative study rooms with chat, video, and productivity tools.
            </p>
            <Link
              to="/google-wave/"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Explore CSW as Google Wave Alternative
              <ArrowRight className="w-4 h-4" />
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GoogleWaveHistory;
